
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, DOCUMENT } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { environment } from '@environment/environment';
import { TranslateService } from '@ngx-translate/core';
import { referencesEnvironment } from '@references/environment/environment.references';
import { PublicHiringFirmsService } from '@references/shared/services/public/hiring-firms.service';
import {
  accessibleTextColorOn,
  darkenToContrast
} from '@shared/helpers/color-contrast.helper';
import { hexToRGB } from '@shared/helpers/utils.helper';
import { IHiringFirm } from '@shared/interfaces/hiring-firm.interface';
import { ISetting } from '@shared/interfaces/setting.interface';
import { CommonEnvironmentsService } from '@shared/services/environments.service';
import { CookieConsentService } from '@shared/services/cookie-consent.service';
import { GooglePlacesLoaderService } from '@shared/services/google-places-loader.service';
import { sentryHelper } from '@shared/helpers/sentry.helper';
import { LocalStorage, SessionStorage } from 'ngx-webstorage';
import { Observable, Observer, of } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class EnvironmentsService {
  @LocalStorage() public lng: string;

  @SessionStorage() private hiringFirm: IHiringFirm;

  public readonly IS_PRODUCTION: boolean = Boolean(environment.IS_PRODUCTION);
  public readonly VERSION: string = String(environment.VERSION);

  public readonly tokenName: string = String('recruiter-token');

  public readonly ENDPOINTS_ASSETS: string = String(
    environment.ENDPOINTS.ASSETS
  );

  public readonly ENDPOINTS_APP: string = String(
    environment.ENDPOINTS.FRONT_END.APP
  );

  public readonly ENDPOINTS_REFERENCES: string = String(
    environment.ENDPOINTS.FRONT_END.REFERENCES
  );

  public HOSTNAME = String(this._commonEnvironments.getHostname());
  public IS_VITAY = Boolean(this.HOSTNAME.indexOf('vitay') > -1);
  public IS_UK_SERVER = Boolean(
    window.location.hostname.indexOf('.co.uk') > -1
  );
  // Build-time hosting region ('us' | 'ca' | 'uk'), see environment.references.*.ts.
  public REGION: string = String(referencesEnvironment.REGION || 'us');

  public IS_HIRING_FIRM_UNLOCKED = Boolean(
    ['vitay', 'mintzscreeningservices'].includes(this.HOSTNAME) || false
  );

  public logo: string;

  constructor(
    @Inject(DOCUMENT) private readonly _document: any,
    private readonly _commonEnvironments: CommonEnvironmentsService,
    private readonly _http: HttpClient,
    private readonly _title: Title,
    private readonly _translate: TranslateService,
    private readonly _publicHiringFirms: PublicHiringFirmsService,
    private readonly _cookieConsent: CookieConsentService,
    private readonly _placesLoader: GooglePlacesLoaderService
  ) {}

  public getIPAddress(): Observable<any> {
    if (this.IS_PRODUCTION) {
      return this._http.get('https://ipinfo.io/json').pipe(
        timeout(5000), // 5 second timeout
        map((res: any) => res),
        catchError((error: any) => {
          // ipinfo.io is a best-effort third party. Its timeouts, dropped
          // connections and 5xx are its outages, not Vitay defects, and the
          // null-IP fallback below already handles them — so they get a
          // breadcrumb rather than a Sentry issue that pages the team.
          // Anything else (e.g. 429 rate limiting) still warrants a look.
          if (this.isExpectedIPLookupFailure(error)) {
            sentryHelper.addBreadcrumb({
              category: 'http',
              level: 'warning',
              message: 'ipinfo.io lookup failed',
              data: {
                url: 'https://ipinfo.io/json',
                status: error && error.status !== undefined ? error.status : null
              }
            });
          } else {
            sentryHelper.captureException(error, {
              tags: {
                component: 'EnvironmentsService',
                method: 'getIPAddress'
              },
              extra: {
                error_message: error.message,
                error_status: error.status,
                url: 'https://ipinfo.io/json'
              }
            });
          }

          // Return null IP so metadata can still be sent with device info
          return of({ ip: null });
        })
      );
    }

    return new Observable((obs: Observer<any>) =>
      obs.next({ ip: '127.0.0.1' })
    );
  }

  // The 5s timeout (no status), a network/CORS failure (status 0) and any
  // upstream 5xx all mean "ipinfo.io is unreachable" — expected and handled.
  private isExpectedIPLookupFailure(error: any): boolean {
    if (error && error.name === 'TimeoutError') {
      return true;
    }

    const status = error && error.status;

    return status === 0 || (status >= 500 && status <= 599);
  }

  public setLng(lng: string): string {
    // Exact-match whitelist: the ?lng= query param is user-controlled and
    // email link scanners (e.g. Proofpoint URL Defense) can append junk to it.
    lng = /^(en|es|fr|fr-ca)$/.test(lng) ? lng : 'en';

    this._translate.setDefaultLang(lng);
    this._translate.use(lng);

    this._document.documentElement.lang = lng;

    // Note: Language is stored per user in the database (users.language field)
    // Each user can have their own language preference
    // This ensures language is personal to each user, not company-wide

    return lng;
  }

  public setTheme() {
    this.setFavicon();
    this.setLogo();
    this.setTitle();
    this.setStylesheet();
  }

  public setStylesheet(settings?: ISetting) {
    let isUpdated = true;
    if (!this.IS_VITAY) {
      if (!settings) {
        isUpdated = false;
        settings = this.hiringFirm?.settings;
      }

      const colorsPrimary = settings?.colors_primary || '#0E7C87';
      const colorsSecondary = settings?.colors_secondary || '#333333';
      const fontFamily =
        settings?.font_family || '"Open Sans", Arial, Helvetica, sans-serif';

      const isWhiteLabelEnable =
        settings?.is_plugins_white_label_enabled || false;

      const colorsPrimaryText = darkenToContrast(colorsPrimary, '#FFFFFF', 4.5);
      const colorsPrimaryButtonText = accessibleTextColorOn(colorsPrimary);

      if (isWhiteLabelEnable) {
        const head = this._document.getElementsByTagName('head')[0];

        const css = `
            html, body { font-family: ${fontFamily} !important; }
            .v-btn-primary { background-color: ${colorsPrimary} !important; color: ${colorsPrimaryButtonText} !important; }
            .v-btn-primary:not(.disabled):hover { background-color: ${colorsSecondary} !important; }
            .v-btn-outline-primary { color: ${colorsPrimaryText} !important; border-color: ${colorsPrimaryText} !important; }
            .v-btn-outline-primary:hover { background-color: ${colorsPrimary} !important; }
            a:hover { color: ${colorsPrimaryText} !important; }
            .page-item .page-link { color: ${colorsPrimaryText} !important; }
            .page-item.active .page-link { color: ${colorsPrimaryButtonText} !important; border-color: ${colorsPrimary} !important; background: ${colorsPrimary} !important; }
            .loading .spinner div { background-color: ${colorsPrimary} !important; }
            .navbar .v-nav-item:hover a i, .navbar .v-nav-item.active a i, .topbar .v-nav-item:hover .nav-link i, .topbar .v-nav-item.active .nav-link i { color: ${colorsPrimaryText} !important; }
            tabset .nav-pills .nav-link.active { background-color: ${hexToRGB(
              colorsPrimary,
              0.15
            )} !important; color: ${colorsPrimaryText} !important; }
            .v-alert-primary { color: ${colorsPrimaryText} !important; background-color: ${hexToRGB(
          colorsPrimary,
          0.4
        )} !important; }
            .integrations-card--isNotAvailable { background-color: ${colorsPrimary} !important; }
            .v-text-primary { color: ${colorsPrimaryText} !important; }
            .v-text--highlight { color: ${colorsPrimaryText} !important; }
            .v-table-hover tbody tr:hover { background-color: ${hexToRGB(
              colorsPrimary,
              0.05
            )} !important; }
            .form-control:focus { background-color: ${hexToRGB(
              colorsPrimary,
              0.1
            )} !important; border-color: ${colorsPrimaryText} !important; }
            .bulk-action-bar__select-all { color: ${colorsPrimaryText} !important; }
            .v-table-select .form-check-input:checked, .v-table-select .form-check-input:indeterminate { background-color: ${colorsPrimary} !important; border-color: ${colorsPrimary} !important; }
            .v-table.table-striped > tbody > tr.v-table-row--selected > :not(.v-table-actions) { --bs-table-accent-bg: ${hexToRGB(
              colorsPrimary,
              0.07
            )} !important; --bs-table-bg-state: ${hexToRGB(
              colorsPrimary,
              0.07
            )}; background-color: ${hexToRGB(
          colorsPrimary,
          0.07
        )} !important; }          `;

        const styleId = 'white-label-stylesheet';
        const style = this._document.createElement('style');
        style.type = 'text/css';
        style.id = styleId;

        style.appendChild(document.createTextNode(css));

        const isStyleExist = !!this._document.getElementById(styleId);

        if (!isStyleExist || isUpdated) {
          head.appendChild(style);
          this.hiringFirm = this.hiringFirm;
        }
      }
    }
  }

  public setScripts() {
    // Google Places (address autocomplete) is a convenience, not strictly
    // necessary — the manual address fields work without it — so the script
    // only loads after functional consent (VIT-1391). Gated in every
    // environment, matching where the consent banner renders.
    this._cookieConsent
      .grantedOnce$('functional')
      .subscribe(() =>
        this._placesLoader.load(referencesEnvironment.GOOGLE_SEARCH.key)
      );

    if (this.IS_PRODUCTION) {
      // Google Consent Mode v2: everything denied until the user opts in.
      this._gtag('consent', 'default', {
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
        analytics_storage: 'denied'
      });

      this._cookieConsent.consent$.subscribe(() => {
        const analytics = this._cookieConsent.isGranted('analytics');
        const advertisement = this._cookieConsent.isGranted('advertisement');

        this._gtag('consent', 'update', {
          ad_storage: advertisement ? 'granted' : 'denied',
          ad_user_data: advertisement ? 'granted' : 'denied',
          ad_personalization: advertisement ? 'granted' : 'denied',
          analytics_storage: analytics ? 'granted' : 'denied'
        });

        if ((analytics || advertisement) && !this._isGtmInjected) {
          this._injectGtm();
        }
      });

      this._cookieConsent
        .grantedOnce$('functional')
        .subscribe(() => this._injectHelpScout());
    }
  }

  private _isGtmInjected: boolean = Boolean(false);

  // gtag() must push the `arguments` object itself, not an array.
  private _gtag(..._args: any[]): void {
    const win = this._document.defaultView as any;
    win.dataLayer = win.dataLayer || [];
    // eslint-disable-next-line prefer-rest-params
    win.dataLayer.push(arguments);
  }

  private _injectGtm(): void {
    this._isGtmInjected = true;

    const head = this._document.getElementsByTagName('head')[0];
    const body = this._document.getElementsByTagName('body')[0];

    const scriptGATag = this._document.createElement('script');
    scriptGATag.type = 'text/javascript';

    let gaTagInnerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});`;
    gaTagInnerHTML += `var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;`;
    gaTagInnerHTML += `j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})`;
    gaTagInnerHTML += `(window, document, 'script', 'dataLayer', '${referencesEnvironment.GOOGLE_ANALYTICS.GTM}');`;

    scriptGATag.innerHTML = gaTagInnerHTML;

    const noScriptGATag = this._document.createElement('noscript');
    noScriptGATag.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${referencesEnvironment.GOOGLE_ANALYTICS.GTM}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;

    body.appendChild(noScriptGATag);
    head.appendChild(scriptGATag);
  }

  private _injectHelpScout(): void {
    const head = this._document.getElementsByTagName('head')[0];

    const script = this._document.createElement('script');
    script.type = 'text/javascript';

    let helpScoutTag = `!function(e,t,n){function a(){var e=t.getElementsByTagName("script")[0],n=t.createElement("script");n.type="text/javascript",n.async=!0,n.src="https://beacon-v2.helpscout.net",e.parentNode.insertBefore(n,e)}
      if(e.Beacon=n=function(t,n,a){e.Beacon.readyQueue.push({method:t,options:n,data:a})},n.readyQueue=[],"complete"===t.readyState)return a();e.attachEvent?e.attachEvent("onload",a):e.addEventListener("load",a,!1)}
      (window,document,window.Beacon||function(){});`;
    helpScoutTag += `window.Beacon('init', 'fc8b4ae7-983d-40c6-be18-529973438445');`;
    helpScoutTag += `window.Beacon('config', { display: { style: 'manual' } });`;

    script.innerHTML = helpScoutTag;
    head.appendChild(script);
  }

  public resolve(): Observable<IHiringFirm[]> {
    if (!this.IS_VITAY && !this.hiringFirm) {
      return this._publicHiringFirms.get();
    }

    return;
  }

  private setFavicon() {
    let href = 'favicon.ico';
    if (!this.IS_VITAY) {
      href = `${this.ENDPOINTS_ASSETS}/images/white-label/${this.HOSTNAME}/favicon.ico`;
    }

    const favicon = this._document.getElementById('appFavicon');
    if (!!favicon) {
      favicon.setAttribute('href', href);
    }
  }

  private setLogo() {
    let url = `${this.ENDPOINTS_ASSETS}/images/white-label/${this.HOSTNAME}/logo.svg`;
    if (this.IS_VITAY) {
      url = `${this.ENDPOINTS_ASSETS}/images/vitay.io/logo-green.svg`;
    }

    return (this.logo = url);
  }

  public get brandName(): string {
    const titles: any = {
      vitay: 'Vitay.io',
      refchx: 'RefChx',
      mintzscreeningservices: 'Mintz Global Services',
      calgarystampede: 'Calgary Stampede',
      screeningcanada: 'Screening Canada',
      plantoprotectscreening: 'Plan To Protect',
      probitypeople: 'ProbityPeople',
      cpr: 'Canadian Pacific Railway',
      mie: 'MIE'
    };

    return titles[this.HOSTNAME] || this.HOSTNAME;
  }

  private setTitle() {
    this._title.setTitle(`${this.brandName} - References checked`);
  }
}
